import React from "react";
import { View, StyleSheet, StyleProp, ViewStyle } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import { StatusBadge } from "./StatusBadge";
import { TimeChip } from "./TimeChip";
import Chip from "@/components/ui/Chip";

interface Position {
  name: string;
  description?: string;
  gifted_hours?: number;
  payroll_rate?: { rate: number };
  invited_users?: { id: string; status: "ACCEPTED" | "DECLINED" | "PENDING" }[];
}

interface PositionCardProps {
  position: Position;
  userId: string;
  totalHours?: number;
  style?: StyleProp<ViewStyle>;
  compact?: boolean;
  shiftDressCode?: { id: string; name: string; description?: string };
  projectDressCode?: { id: string; name: string; description?: string };
}

export function PositionCard({
  position,
  userId,
  totalHours = 0,
  style,
  compact = false,
  shiftDressCode,
  projectDressCode,
}: PositionCardProps) {
  const { colors } = useTheme();

  const userInvite = position.invited_users?.find((user) => user.id === userId);
  const status = userInvite?.status || "PENDING";
  const rate = position.payroll_rate?.rate || 0;
  const giftedHours = position.gifted_hours || 0;
  const minEarnings = giftedHours * rate;
  const maxEarnings = totalHours * rate;

  // Determine dress code (shift takes precedence, fallback to project)
  const dressCode = shiftDressCode || projectDressCode;
  // const isDressCodeFromProject = !shiftDressCode && !!projectDressCode;

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "ACCEPTED":
        return "accepted";
      case "DECLINED":
        return "declined";
      default:
        return "pending";
    }
  };

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: colors.backgroundSecondary,
          borderColor: colors.border,
          padding: compact ? 12 : 16,
        },
        style,
      ]}
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <MaterialCommunityIcons name="account-tie" size={20} color={colors.text} />
          <ThemedText style={[styles.positionName, { fontSize: compact ? 14 : 16 }]}>{position.name}</ThemedText>
          <StatusBadge variant={getStatusVariant(status)} text={status} size={compact ? "small" : "medium"} />
        </View>
      </View>

      {/* Description */}
      {position.description && !compact && (
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>{position.description}</ThemedText>
      )}

      {/* Details */}
      <View style={styles.details}>
        {/* Rate */}
        {rate > 0 && <TimeChip type="earnings" text={`$${rate}/hr`} size={compact ? "small" : "medium"} />}

        {/* Gifted Hours */}
        {giftedHours > 0 && (
          <View style={styles.detailItem}>
            <MaterialCommunityIcons
              name="gift-outline"
              size={compact ? 12 : 14}
              color={colors.blue?.text || colors.text}
            />
            <ThemedText style={[styles.detailText, { fontSize: compact ? 11 : 12 }]}>
              {giftedHours}h guaranteed
            </ThemedText>
          </View>
        )}

        {/* Dress Code */}
        {dressCode && !compact && (
          <View style={styles.dressCodeContainer}>
            <ThemedText>Dress Code</ThemedText>
            <View style={styles.dressCodeChip}>
              <MaterialCommunityIcons name="tshirt-crew" size={12} color="#ffffff" style={{ marginRight: 4 }} />
              <ThemedText style={styles.dressCodeText}>{dressCode.name}</ThemedText>
            </View>
            {/* {isDressCodeFromProject && ( */}
            {/*   <Chip */}
            {/*     text="From Project" */}
            {/*     style={{ */}
            {/*       paddingHorizontal: 6, */}
            {/*       paddingVertical: 2, */}
            {/*       backgroundColor: colors.cyan?.background || "#06b6d4cc", */}
            {/*       borderColor: colors.cyan?.border || "#06b6d4", */}
            {/*     }} */}
            {/*   /> */}
            {/* )} */}
          </View>
        )}

        {/* Dress Code Description */}
        {dressCode?.description && !compact && (
          <View style={styles.dressCodeDescription}>
            <ThemedText style={[styles.dressCodeDescriptionText, { color: colors.textSecondary }]}>
              {dressCode.description}
            </ThemedText>
          </View>
        )}

        {/* Earnings Range */}
        {rate > 0 && totalHours > 0 && !compact && (
          <View style={styles.earningsRange}>
            <ThemedText style={[styles.earningsText, { color: colors.textSecondary }]}>
              ${minEarnings.toFixed(0)} - ${maxEarnings.toFixed(0)} potential
            </ThemedText>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
  },
  header: {
    marginBottom: 8,
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    justifyContent: "space-between",
  },
  positionName: {
    fontWeight: "600",
    flex: 1,
    marginRight: 8,
  },
  description: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 8,
  },
  details: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
  },
  detailItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  detailText: {
    fontWeight: "500",
    color: "#3b82f6",
  },
  earningsRange: {
    marginTop: 4,
    width: "100%",
  },
  earningsText: {
    fontSize: 12,
    fontWeight: "500",
  },
  dressCodeContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  dressCodeChip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    backgroundColor: "#8b5cf6",
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: "#7c3aed",
  },
  dressCodeText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#ffffff",
    letterSpacing: 0.3,
  },
  dressCodeDescription: {
    width: "100%",
    marginTop: 4,
  },
  dressCodeDescriptionText: {
    fontSize: 11,
    lineHeight: 16,
  },
});
